import { KioskLocation, KioskEvse, KioskConnector } from '../types/assign-kiosk-types';

/**
 * Builds the double-serialized settings JSON required by API #4.
 *
 * Rules:
 * - ALL connectors from the location are included (not just selected ones).
 * - Selected connectors get isActive: true, unselected get isActive: false.
 * - alias is included only when non-empty.
 * - index (displayOrder) is included only when not null.
 * - Any existing top-level keys from the previous settings string are preserved.
 *
 * The returned value is the inner JSON string — the caller must wrap it as:
 *   { settings: buildSettingsJson(...) }
 */
export function buildSettingsJson(
  location: KioskLocation,
  existingSettings: string | null = null
): string {
  // Preserve any existing top-level settings keys (e.g. future fields)
  let settingsMap: Record<string, unknown> = {};
  if (existingSettings) {
    try {
      settingsMap = JSON.parse(existingSettings) as Record<string, unknown>;
    } catch {
      settingsMap = {};
    }
  }

  const filter: Record<string, unknown>[] = [];

  for (const evse of location.evses) {
    if (!evse.uid) continue;

    for (const connector of evse.connectors) {
      if (!connector.id) continue;

      const item: Record<string, unknown> = {
        evseUid: evse.uid,
        connectorId: connector.id,
        isActive: connector.isSelected,
      };

      if (connector.alias && connector.alias.trim().length > 0) {
        item.alias = connector.alias.trim();
      }

      if (connector.displayOrder != null) {
        item.index = connector.displayOrder;
      }

      filter.push(item);
    }
  }

  settingsMap.filter = filter;
  return JSON.stringify(settingsMap);
}

/**
 * Composite key used throughout the feature to uniquely identify a connector.
 * Format: "{evseUid}:{connectorId}"
 */
export function compositeKey(evseUid: string, connectorId: string): string {
  return `${evseUid}:${connectorId}`;
}

/**
 * Returns the list of display-order values already used by selected connectors
 * in a given location, excluding the specified connector (so it can re-use its own value).
 */
export function getUsedOrders(
  location: KioskLocation,
  excludeEvseUid: string,
  excludeConnectorId: string
): number[] {
  const used: number[] = [];
  for (const evse of location.evses) {
    for (const connector of evse.connectors) {
      if (evse.uid === excludeEvseUid && connector.id === excludeConnectorId) continue;
      if (connector.displayOrder != null) {
        used.push(connector.displayOrder);
      }
    }
  }
  return used;
}
